import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  setDoc,
} from "firebase/firestore";
import { FaEdit, FaTrash } from "react-icons/fa";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission, ROLES, FEATURES } from "../../lib/rolesConfig";
import MultiActionScrollButton from "../MultiActionScrollButton";
import {
  commitFirestoreWrite,
  isOffline,
  offlineAwareMessage,
} from "../../utils/offline";

const formatTimestampForDateTimeLocalInput = (timestamp) => {
  if (timestamp && timestamp.toDate) {
    const date = timestamp.toDate();
    // Adjust for timezone offset to display in user's local time
    const timezoneOffset = date.getTimezoneOffset() * 60000;
    const localDate = new Date(date.getTime() - timezoneOffset);
    return localDate.toISOString().slice(0, 16);
  }
  return "";
};

const formatAdministeredAt = (timestamp) => {
  if (!timestamp || !timestamp.toDate) return "N/A";
  try {
    const jsDate = timestamp.toDate();
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
      .format(jsDate)
      .replace(",", "");
  } catch (error) {
    console.error("Error formatting date:", error);
    return "Invalid Date";
  }
};

const LOG_STATUSES = {
  GIVEN: "Given",
  HELD: "Held",
  SKIPPED: "Skipped",
};

const getLogStatus = (entry) => entry?.status || LOG_STATUSES.GIVEN;

const getStatusBadgeClasses = (status) => {
  if (status === LOG_STATUSES.HELD) {
    return "bg-amber-100 text-amber-800";
  }
  if (status === LOG_STATUSES.SKIPPED) {
    return "bg-slate-100 text-slate-700";
  }
  return "bg-green-100 text-green-800";
};

const formatBloodPressure = (bp) => {
  if (!bp) return "";
  return `${bp.systolic ?? "-"}/${bp.diastolic ?? "-"}`;
};

const toInventoryNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const MedicationAdministration = () => {
  const { currentUser, activeCareProfile, userRole } = useAuth();
  const navigate = useNavigate();
  const [medicationOptions, setMedicationOptions] = useState([]);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [showAddNewMedication, setShowAddNewMedication] = useState(false);
  const [newMedicationName, setNewMedicationName] = useState("");
  const [isCreatingDraftMedication, setIsCreatingDraftMedication] =
    useState(false);
  const newMedicationNameInputRef = useRef(null);
  const initialLogMedAdminState = {
    selectedMedicationId: "", // used for editing a single existing log entry
    selectedMedicationIds: [], // used for creating new logs for multiple medications
    selectedMedicationDosages: {}, // map: medicationId -> dosage for multi-select
    logDosage: "",
    administeredAtDateTime: "",
    logNotes: "",
    medicationName: "",
    frequencyExpected: "",
    administrationStatus: LOG_STATUSES.GIVEN,
    holdReason: "",
  };
  const [logMedAdmin, setLogMedAdmin] = useState(initialLogMedAdminState);
  const [isLoadingMedicationOptions, setIsLoadingMedicationOptions] =
    useState(true);
  const [administrationHistory, setAdministrationHistory] = useState([]);
  const [isLoadingAdministrationHistory, setIsLoadingAdministrationHistory] =
    useState(true);
  const [isEditingLogEntry, setIsEditingLogEntry] = useState(false);
  const [currentLogEntryId, setCurrentLogEntryId] = useState(null);
  const [showLogAdministrationForm, setShowLogAdministrationForm] =
    useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [visibleHistoryCount, setVisibleHistoryCount] = useState(5);
  const formRef = useRef(null);

  const canWrite =
    activeCareProfile && userRole && currentUser
      ? hasPermission(
          userRole[activeCareProfile.id] ||
            (activeCareProfile.userId === currentUser.uid
              ? ROLES.OWNER
              : undefined),
          FEATURES.MEDICATION_ADMINISTRATION,
          "write",
        )
      : false;

  useEffect(() => {
    // Track viewport size to render mobile-friendly selectors
    try {
      const mediaQuery = window.matchMedia("(max-width: 639px)");
      const updateIsSmall = () => setIsSmallScreen(mediaQuery.matches);
      updateIsSmall();
      mediaQuery.addEventListener("change", updateIsSmall);
      return () => mediaQuery.removeEventListener("change", updateIsSmall);
    } catch (e) {
      // no-op for environments without window
      setIsSmallScreen(false);
    }
  }, []);

  useEffect(() => {
    if (!showAddNewMedication) return;

    // Defer to ensure the input is mounted before focusing.
    const t = window.setTimeout(() => {
      const el = newMedicationNameInputRef.current;
      if (!el) return;
      try {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch (e) {
        // ignore
      }
      try {
        el.focus();
      } catch (e) {
        // ignore
      }
    }, 0);

    return () => window.clearTimeout(t);
  }, [showAddNewMedication]);

  useEffect(() => {
    if (!activeCareProfile) {
      setIsLoadingMedicationOptions(false);
      setIsLoadingAdministrationHistory(false);
      setMedicationOptions([]);
      setAdministrationHistory([]);
      return;
    }

    setIsLoadingMedicationOptions(true);
    const medOptionsQuery = query(
      collection(db, "medications"),
      where("careProfileId", "==", activeCareProfile.id),
      where("status", "in", ["Active", "PRN", "Draft"]),
      orderBy("medicationName", "asc"),
    );
    const unsubscribeOptions = onSnapshot(
      medOptionsQuery,
      (snapshot) => {
        const options = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setMedicationOptions(options);
        setIsLoadingMedicationOptions(false);
      },
      (error) => {
        console.error("Error fetching medication options: ", error);
        setIsLoadingMedicationOptions(false);
      },
    );

    setIsLoadingAdministrationHistory(true);
    const historyQuery = query(
      collection(db, "medicationAdministrationLog"),
      where("careProfileId", "==", activeCareProfile.id),
      orderBy("administeredAt", "desc"),
    );
    const unsubscribeHistory = onSnapshot(
      historyQuery,
      (snapshot) => {
        const history = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setAdministrationHistory(history);
        setIsLoadingAdministrationHistory(false);
      },
      (error) => {
        console.error("Error fetching administration history: ", error);
        setIsLoadingAdministrationHistory(false);
      },
    );

    return () => {
      unsubscribeOptions();
      unsubscribeHistory();
    };
  }, [activeCareProfile]);

  const isDraftMed = (med) =>
    String(med?.status || "").toLowerCase() === "draft";

  const getMedicationOptionLabel = (med) => {
    const name = med?.medicationName || "Unnamed medicine";
    return isDraftMed(med) ? `${name} (Draft)` : name;
  };

  const handleFinishSetupForMedicationId = (medicationId) => {
    if (!activeCareProfile || !medicationId) return;
    const pendingKey = `mila:pendingFinishDraftMed:${activeCareProfile.id}`;
    try {
      window.localStorage.setItem(pendingKey, medicationId);
    } catch (e) {
      // ignore
    }
    navigate("/medications/active");
  };

  const handleLogInputChange = (e) => {
    const { name, value } = e.target;
    setLogMedAdmin((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleLogMedicationSelectionChange = (e) => {
    const medId = e.target.value;
    if (medId === "__add_new__") {
      setShowAddNewMedication(true);
      // keep existing selection
      return;
    }
    const selectedOption = medicationOptions.find((opt) => opt.id === medId);
    if (selectedOption) {
      setLogMedAdmin((prevState) => ({
        ...prevState,
        selectedMedicationId: medId,
        logDosage: selectedOption.strength || "",
        medicationName: selectedOption.medicationName,
        // Firestore doesn't allow undefined; Draft meds may not have frequency yet.
        frequencyExpected: selectedOption.frequency || "",
      }));
    } else {
      setLogMedAdmin((prevState) => ({
        ...initialLogMedAdminState,
        administeredAtDateTime: prevState.administeredAtDateTime,
        logNotes: prevState.logNotes,
      }));
    }
  };

  const handleLogMedicationMultiSelectionChange = (e) => {
    const selectedIds = Array.from(e.target.selectedOptions).map(
      (opt) => opt.value,
    );

    if (selectedIds.includes("__add_new__")) {
      setShowAddNewMedication(true);
      // remove the sentinel from selection
      const filtered = selectedIds.filter((id) => id !== "__add_new__");
      // Update selection without losing existing dosage entries
      setLogMedAdmin((prevState) => {
        const updatedDosages = {
          ...(prevState.selectedMedicationDosages || {}),
        };
        // remove dosages for meds no longer selected
        Object.keys(updatedDosages).forEach((id) => {
          if (!filtered.includes(id)) delete updatedDosages[id];
        });
        return {
          ...prevState,
          selectedMedicationIds: filtered,
          selectedMedicationDosages: updatedDosages,
        };
      });
      return;
    }

    setLogMedAdmin((prevState) => {
      const updatedDosages = {};
      selectedIds.forEach((id) => {
        const existing = prevState.selectedMedicationDosages?.[id];
        if (existing && String(existing).trim() !== "") {
          updatedDosages[id] = existing;
        } else {
          const med = medicationOptions.find((m) => m.id === id);
          updatedDosages[id] = med?.strength || "";
        }
      });
      return {
        ...prevState,
        selectedMedicationIds: selectedIds,
        selectedMedicationDosages: updatedDosages,
      };
    });
  };

  const createDraftMedicationFromLogs = async () => {
    if (!canWrite) {
      window.alert("You do not have permission to perform this action.");
      return;
    }
    if (!activeCareProfile) {
      window.alert("No care profile selected.");
      return;
    }
    const trimmedName = String(newMedicationName || "").trim();
    if (!trimmedName) {
      window.alert("Please enter a medication name.");
      return;
    }

    setIsCreatingDraftMedication(true);
    const actor = {
      uid: currentUser.uid,
      name: currentUser.displayName || currentUser.email,
    };

    const payload = {
      medicationName: trimmedName,
      status: "Draft",
      careProfileId: activeCareProfile.id,
      userId: activeCareProfile.userId,
      createdAt: Timestamp.now(),
      createdBy: actor,
      lastModifiedAt: Timestamp.now(),
      lastModifiedBy: actor,
    };

    try {
      const docRef = doc(collection(db, "medications"));
      await commitFirestoreWrite(
        setDoc(docRef, payload),
        "Create draft medication from administration log",
      );
      const newId = docRef.id;

      // auto-select the newly created draft in the current form
      setLogMedAdmin((prev) => {
        if (isEditingLogEntry) {
          return prev;
        }

        if (isSmallScreen) {
          const prevIds = prev.selectedMedicationIds || [];
          const nextIds = prevIds.includes(newId)
            ? prevIds
            : [...prevIds, newId];
          return {
            ...prev,
            selectedMedicationIds: nextIds,
            selectedMedicationDosages: {
              ...(prev.selectedMedicationDosages || {}),
              [newId]: prev.selectedMedicationDosages?.[newId] || "",
            },
          };
        }

        // desktop multi-select path uses selectedMedicationIds
        const prevIds = prev.selectedMedicationIds || [];
        const nextIds = prevIds.includes(newId) ? prevIds : [...prevIds, newId];
        return {
          ...prev,
          selectedMedicationIds: nextIds,
          selectedMedicationDosages: {
            ...(prev.selectedMedicationDosages || {}),
            [newId]: prev.selectedMedicationDosages?.[newId] || "",
          },
        };
      });

      setNewMedicationName("");
      setShowAddNewMedication(false);
    } catch (error) {
      console.error("Error creating draft medication from logs: ", error);
      window.alert("Failed to add new medicine.");
    } finally {
      setIsCreatingDraftMedication(false);
    }
  };

  const handlePerMedicationDosageChange = (medicationId, value) => {
    setLogMedAdmin((prevState) => ({
      ...prevState,
      selectedMedicationDosages: {
        ...prevState.selectedMedicationDosages,
        [medicationId]: value,
      },
    }));
  };

  const handleToggleMedicationCheckbox = (medicationId, checked) => {
    setLogMedAdmin((prevState) => {
      const wasSelected = (prevState.selectedMedicationIds || []).includes(
        medicationId,
      );
      let nextIds = prevState.selectedMedicationIds || [];
      let nextDosages = { ...(prevState.selectedMedicationDosages || {}) };
      if (checked && !wasSelected) {
        nextIds = [...nextIds, medicationId];
        if (
          !nextDosages[medicationId] ||
          String(nextDosages[medicationId]).trim() === ""
        ) {
          const med = medicationOptions.find((m) => m.id === medicationId);
          nextDosages[medicationId] = med?.strength || "";
        }
      } else if (!checked && wasSelected) {
        nextIds = nextIds.filter((id) => id !== medicationId);
        // Clean up dosage for unselected med to avoid stale validation states
        delete nextDosages[medicationId];
      }
      return {
        ...prevState,
        selectedMedicationIds: nextIds,
        selectedMedicationDosages: nextDosages,
      };
    });
  };

  const getAdminName = async () => {
    let adminName = currentUser.displayName || currentUser.email || "Unknown";
    try {
      const userDocRef = doc(db, "users", currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists() && userDoc.data().fullName) {
        adminName = userDoc.data().fullName;
      }
    } catch (error) {
      console.error("Error fetching user's full name: ", error);
    }
    return adminName;
  };

  const deductInventoryForAdministration = async ({
    medication,
    logId,
    administeredAt,
    actor,
  }) => {
    if (!activeCareProfile || !medication?.id) return;

    try {
      const inventoryConstraints = medication.linkedInventoryId
        ? [
            where("careProfileId", "==", activeCareProfile.id),
            where("__name__", "==", medication.linkedInventoryId),
          ]
        : [
            where("careProfileId", "==", activeCareProfile.id),
            where("medicationId", "==", medication.id),
          ];
      const inventorySnapshot = await getDocs(
        query(collection(db, "medicationInventory"), ...inventoryConstraints),
      );
      if (inventorySnapshot.empty) return;

      const inventoryDoc = inventorySnapshot.docs[0];
      const inventoryItem = {
        id: inventoryDoc.id,
        ...inventoryDoc.data(),
      };
      const quantityBefore = toInventoryNumber(inventoryItem.quantityOnHand);
      const deduction = toInventoryNumber(
        inventoryItem.deductQuantityPerAdministration,
        1,
      );
      if (deduction <= 0) return;

      const quantityAfter = Math.max(0, quantityBefore - deduction);
      const now = Timestamp.now();

      await updateDoc(doc(db, "medicationInventory", inventoryItem.id), {
        quantityOnHand: quantityAfter,
        lastModifiedAt: now,
        lastModifiedBy: actor,
      });

      await addDoc(collection(db, "medicationInventoryAdjustments"), {
        careProfileId: activeCareProfile.id,
        medicationInventoryId: inventoryItem.id,
        medicationId: medication.id,
        medicationName: medication.medicationName,
        source: "administration",
        sourceId: logId,
        delta: quantityAfter - quantityBefore,
        quantityBefore,
        quantityAfter,
        reason: `Given administration logged at ${administeredAt
          .toDate()
          .toLocaleString()}`,
        createdAt: now,
        createdBy: actor,
      });
    } catch (error) {
      console.error("Error deducting medication inventory:", error);
    }
  };

  const handleLogMedicationAdministration = async (e) => {
    e.preventDefault();
    if (!canWrite) {
      window.alert("You don't have permission to perform this action.");
      return;
    }
    const hasSelectedMedications = isEditingLogEntry
      ? !!logMedAdmin.selectedMedicationId
      : Array.isArray(logMedAdmin.selectedMedicationIds) &&
        logMedAdmin.selectedMedicationIds.length > 0;

    if (!hasSelectedMedications || !logMedAdmin.administeredAtDateTime) {
      window.alert("Please select medication(s) and administration time.");
      return;
    }

    const selectedLogStatus =
      logMedAdmin.administrationStatus || LOG_STATUSES.GIVEN;
    const requiresDose = selectedLogStatus === LOG_STATUSES.GIVEN;
    const requiresReason =
      selectedLogStatus === LOG_STATUSES.HELD ||
      selectedLogStatus === LOG_STATUSES.SKIPPED;

    if (isEditingLogEntry && requiresDose && !logMedAdmin.logDosage) {
      window.alert("Please enter a dosage.");
      return;
    }

    if (!isEditingLogEntry && requiresDose) {
      const missingDosages = (logMedAdmin.selectedMedicationIds || []).filter(
        (id) =>
          !String(logMedAdmin.selectedMedicationDosages?.[id] || "").trim(),
      );
      if (missingDosages.length > 0) {
        window.alert(
          "Please enter a dosage for each selected medication before saving.",
        );
        return;
      }
    }

    if (
      !isEditingLogEntry &&
      requiresReason &&
      !String(logMedAdmin.holdReason || "").trim()
    ) {
      window.alert("Please enter a reason for held/skipped medication.");
      return;
    }

    const selectedOption = medicationOptions.find(
      (opt) => opt.id === logMedAdmin.selectedMedicationId,
    );
    if (!isEditingLogEntry) {
      const validSelectedMedications = medicationOptions.filter((opt) =>
        (logMedAdmin.selectedMedicationIds || []).includes(opt.id),
      );
      if (validSelectedMedications.length === 0) {
        window.alert("Selected medication(s) are no longer active or valid.");
        return;
      }
    } else if (!selectedOption) {
      window.alert("Selected medication is no longer active or valid.");
      return;
    }

    const adminName = await getAdminName();

    try {
      if (isEditingLogEntry) {
        const updatePayload = {
          medicationId: logMedAdmin.selectedMedicationId,
          medicationName: logMedAdmin.medicationName,
          dosageAdministered: requiresDose ? logMedAdmin.logDosage : "",
          // Firestore doesn't allow undefined; Draft meds may not have frequency yet.
          frequencyExpected: logMedAdmin.frequencyExpected || "",
          administeredAt: Timestamp.fromDate(
            new Date(logMedAdmin.administeredAtDateTime),
          ),
          status: selectedLogStatus,
          holdReason: requiresReason ? logMedAdmin.holdReason : "",
          vitalContext: logMedAdmin.vitalContext || null,
          notes: logMedAdmin.logNotes,
          careProfileId: activeCareProfile.id,
          lastModifiedAt: Timestamp.now(),
          lastModifiedBy: {
            uid: currentUser.uid,
            name: adminName,
          },
        };
        const logDocRef = doc(
          db,
          "medicationAdministrationLog",
          currentLogEntryId,
        );
        await commitFirestoreWrite(
          updateDoc(logDocRef, updatePayload),
          "Update medication administration log",
        );
        window.alert(
          offlineAwareMessage("Administration log updated successfully!"),
        );
      } else {
        const nowTs = Timestamp.now();
        const administeredAtTs = Timestamp.fromDate(
          new Date(logMedAdmin.administeredAtDateTime),
        );
        const selectedMedications = medicationOptions.filter((opt) =>
          (logMedAdmin.selectedMedicationIds || []).includes(opt.id),
        );

        const addPromises = selectedMedications.map(async (med) => {
          const createPayload = {
            medicationId: med.id,
            medicationName: med.medicationName,
            linkedInventoryId: med.linkedInventoryId || null,
            dosageAdministered: requiresDose
              ? logMedAdmin.selectedMedicationDosages?.[med.id] ||
                med.strength ||
                ""
              : "",
            // Firestore doesn't allow undefined; Draft meds may not have frequency yet.
            frequencyExpected: med.frequency || "",
            administeredAt: administeredAtTs,
            status: selectedLogStatus,
            holdReason: requiresReason ? logMedAdmin.holdReason : "",
            vitalContext: null,
            notes: logMedAdmin.logNotes,
            careProfileId: activeCareProfile.id,
            userId: activeCareProfile.userId,
            createdAt: nowTs,
            createdBy: {
              uid: currentUser.uid,
              name: adminName,
            },
            lastModifiedAt: nowTs,
            lastModifiedBy: {
              uid: currentUser.uid,
              name: adminName,
            },
          };
          const logRef = doc(collection(db, "medicationAdministrationLog"));
          const result = await commitFirestoreWrite(
            setDoc(logRef, createPayload),
            "Create medication administration log",
          );
          if (selectedLogStatus === LOG_STATUSES.GIVEN) {
            const inventoryWrite = deductInventoryForAdministration({
              medication: med,
              logId: logRef.id,
              administeredAt: administeredAtTs,
              actor: createPayload.createdBy,
            });
            if (result.queued || isOffline()) {
              inventoryWrite.catch((error) => {
                console.error("Queued inventory deduction failed:", error);
              });
            } else {
              await inventoryWrite;
            }
          }
          return logRef;
        });

        await Promise.all(addPromises);
        window.alert(
          offlineAwareMessage(
            `${selectedMedications.length} medication${selectedMedications.length > 1 ? "s" : ""} logged successfully!`,
          ),
        );
      }
      cancelEditLogEntry();
    } catch (error) {
      console.error("Error saving administration log: ", error);
      window.alert("Failed to save log.");
    }
  };

  const cancelEditLogEntry = () => {
    setLogMedAdmin(initialLogMedAdminState);
    setIsEditingLogEntry(false);
    setCurrentLogEntryId(null);
    setShowLogAdministrationForm(false);
  };

  const handleEditLogEntry = (logEntry) => {
    if (!canWrite) {
      window.alert("You don't have permission to edit log entries.");
      return;
    }
    setIsEditingLogEntry(true);
    setCurrentLogEntryId(logEntry.id);
    setLogMedAdmin({
      selectedMedicationId: logEntry.medicationId,
      logDosage: logEntry.dosageAdministered,
      administeredAtDateTime: formatTimestampForDateTimeLocalInput(
        logEntry.administeredAt,
      ),
      logNotes: logEntry.notes || "",
      medicationName: logEntry.medicationName,
      frequencyExpected: logEntry.frequencyExpected,
      administrationStatus: getLogStatus(logEntry),
      holdReason: logEntry.holdReason || "",
      vitalContext: logEntry.vitalContext || null,
    });
    setShowLogAdministrationForm(true);
    setTimeout(
      () => formRef.current?.scrollIntoView({ behavior: "smooth" }),
      100,
    );
  };

  const handleDeleteLogEntry = async (logId) => {
    if (!canWrite) {
      window.alert("You don't have permission to delete log entries.");
      return;
    }
    if (window.confirm("Are you sure you want to delete this log entry?")) {
      try {
        await commitFirestoreWrite(
          deleteDoc(doc(db, "medicationAdministrationLog", logId)),
          "Delete medication administration log",
        );
        window.alert(offlineAwareMessage("Log entry deleted successfully."));
        if (logId === currentLogEntryId) {
          cancelEditLogEntry();
        }
      } catch (error) {
        console.error("Error deleting administration log:", error);
        window.alert("Failed to delete log entry.");
      }
    }
  };

  const toggleShowLogAdministrationForm = () => {
    if (!canWrite) {
      window.alert("You don't have permission to log administrations.");
      return;
    }
    if (showLogAdministrationForm) {
      cancelEditLogEntry();
    } else {
      setShowLogAdministrationForm(true);
      setLogMedAdmin({
        ...initialLogMedAdminState,
        administeredAtDateTime: formatTimestampForDateTimeLocalInput(
          Timestamp.now(),
        ),
      });
    }
  };

  const handleLoadMore = () => {
    setVisibleHistoryCount((prevCount) => prevCount + 5);
  };

  const filteredHistory = administrationHistory.filter(
    (entry) =>
      entry.medicationName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getLogStatus(entry).toLowerCase().includes(searchTerm.toLowerCase()) ||
      (entry.holdReason &&
        entry.holdReason.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (entry.notes &&
        entry.notes.toLowerCase().includes(searchTerm.toLowerCase())),
  );

  return (
    <div>
      <MultiActionScrollButton scrollElementId="main-content" />
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4">
        <h2 className="text-2xl font-semibold text-gray-700 mb-2 sm:mb-0">
          Administration History
        </h2>
        {canWrite && (
          <button
            onClick={toggleShowLogAdministrationForm}
            className="w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg shadow-md transition-colors duration-300 flex items-center justify-center"
          >
            {showLogAdministrationForm ? "Cancel" : "Log Administration"}
          </button>
        )}
      </div>

      {showLogAdministrationForm && (
        <div
          ref={formRef}
          className="bg-gray-50 p-6 rounded-lg shadow-inner mb-8 border border-gray-200"
        >
          <h3 className="text-xl font-semibold text-gray-800 mb-4">
            {isEditingLogEntry ? "Edit" : "Log New"} Administration
          </h3>
          <form onSubmit={handleLogMedicationAdministration}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor={
                    isEditingLogEntry
                      ? "selectedMedicationId"
                      : "selectedMedicationIds"
                  }
                  className="block text-sm font-medium text-gray-700"
                >
                  Medication{!isEditingLogEntry ? "(s)" : ""}
                </label>
                {isEditingLogEntry ? (
                  <select
                    id="selectedMedicationId"
                    name="selectedMedicationId"
                    value={logMedAdmin.selectedMedicationId}
                    onChange={handleLogMedicationSelectionChange}
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                    required
                    disabled={true}
                  >
                    <option value="">
                      {isLoadingMedicationOptions
                        ? "Loading..."
                        : "Select a medication"}
                    </option>
                    {!isLoadingMedicationOptions && canWrite && (
                      <option value="__add_new__">+ Add new medicine…</option>
                    )}
                    {!isLoadingMedicationOptions &&
                      medicationOptions.map((med) => (
                        <option key={med.id} value={med.id}>
                          {getMedicationOptionLabel(med)}
                        </option>
                      ))}
                  </select>
                ) : isSmallScreen ? (
                  <div className="mt-1 grid grid-cols-1 gap-2">
                    {canWrite && (
                      <button
                        type="button"
                        onClick={() => setShowAddNewMedication(true)}
                        className="w-full rounded-md border border-dashed border-gray-300 bg-white px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                      >
                        + Add new medicine…
                      </button>
                    )}
                    {!isLoadingMedicationOptions &&
                      medicationOptions.map((med) => {
                        const checked = (
                          logMedAdmin.selectedMedicationIds || []
                        ).includes(med.id);
                        return (
                          <label
                            key={med.id}
                            className="flex items-center space-x-3 p-2 border rounded-md"
                          >
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                              checked={checked}
                              onChange={(e) =>
                                handleToggleMedicationCheckbox(
                                  med.id,
                                  e.target.checked,
                                )
                              }
                            />
                            <span className="text-sm text-gray-800 flex-1">
                              {getMedicationOptionLabel(med)}
                            </span>
                            <span className="text-xs text-gray-500">
                              {med.frequency || (isDraftMed(med) ? "" : "")}
                            </span>
                          </label>
                        );
                      })}
                  </div>
                ) : (
                  <select
                    multiple
                    id="selectedMedicationIds"
                    name="selectedMedicationIds"
                    value={logMedAdmin.selectedMedicationIds}
                    onChange={handleLogMedicationMultiSelectionChange}
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                    required
                  >
                    {canWrite && (
                      <option value="__add_new__">+ Add new medicine…</option>
                    )}
                    {!isLoadingMedicationOptions &&
                      medicationOptions.map((med) => (
                        <option key={med.id} value={med.id}>
                          {getMedicationOptionLabel(med)}
                        </option>
                      ))}
                  </select>
                )}

                {showAddNewMedication && canWrite && !isEditingLogEntry && (
                  <div className="mt-2 rounded-md border border-gray-200 bg-white p-3">
                    <div className="text-xs text-gray-600 mb-2">
                      Enter a medicine name. We’ll save it as a{" "}
                      <span className="font-semibold">Draft</span> and you can
                      finish setup later.
                    </div>
                    <div className="flex flex-col gap-2 md:flex-row md:items-center">
                      <input
                        type="text"
                        value={newMedicationName}
                        onChange={(e) => setNewMedicationName(e.target.value)}
                        placeholder="e.g., Paracetamol"
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                        disabled={isCreatingDraftMedication}
                        ref={newMedicationNameInputRef}
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={createDraftMedicationFromLogs}
                          className="rounded-md bg-gray-900 px-3 py-2 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-60"
                          disabled={isCreatingDraftMedication}
                        >
                          {isCreatingDraftMedication ? "Adding..." : "Add"}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowAddNewMedication(false);
                            setNewMedicationName("");
                          }}
                          className="rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-200"
                          disabled={isCreatingDraftMedication}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Draft helper */}
                {!isLoadingMedicationOptions &&
                  (() => {
                    const selectedIds = isEditingLogEntry
                      ? [logMedAdmin.selectedMedicationId].filter(Boolean)
                      : logMedAdmin.selectedMedicationIds || [];
                    const selectedDrafts = selectedIds
                      .map((id) => medicationOptions.find((m) => m.id === id))
                      .filter((m) => m && isDraftMed(m));

                    if (selectedDrafts.length === 0) return null;

                    return (
                      <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900">
                        <div className="text-xs">
                          Selected medicine
                          {selectedDrafts.length === 1 ? " is" : "s are"} a{" "}
                          <span className="font-semibold">Draft</span>. You can
                          log it now, and finish setup later to add it to
                          Active/PRN meds.
                        </div>
                        {selectedDrafts.length === 1 && (
                          <button
                            type="button"
                            onClick={() =>
                              handleFinishSetupForMedicationId(
                                selectedDrafts[0].id,
                              )
                            }
                            className="mt-2 w-full rounded-lg bg-amber-600 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-700 md:w-auto"
                          >
                            Finish setup
                          </button>
                        )}
                      </div>
                    );
                  })()}
                {!isEditingLogEntry && !isSmallScreen && (
                  <p className="mt-1 text-xs text-gray-500">
                    Hold Ctrl (Cmd on Mac) to select multiple medications.
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="logDosage"
                  className="block text-sm font-medium text-gray-700"
                >
                  Dosage
                </label>
                {isEditingLogEntry ? (
                  <input
                    type="text"
                    id="logDosage"
                    name="logDosage"
                    value={logMedAdmin.logDosage}
                    onChange={handleLogInputChange}
                    className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    placeholder="e.g., 1 tablet"
                    required={
                      logMedAdmin.administrationStatus === LOG_STATUSES.GIVEN
                    }
                  />
                ) : (
                  <div className="space-y-2">
                    {(logMedAdmin.selectedMedicationIds || []).map((id) => {
                      const med = medicationOptions.find((m) => m.id === id);
                      return (
                        <div key={id} className="flex items-center space-x-2">
                          <span className="text-sm text-gray-700 flex-1">
                            {med?.medicationName || id}
                          </span>
                          <input
                            type="text"
                            value={
                              logMedAdmin.selectedMedicationDosages?.[id] || ""
                            }
                            onChange={(e) =>
                              handlePerMedicationDosageChange(
                                id,
                                e.target.value,
                              )
                            }
                            className="mt-1 block w-48 shadow-sm sm:text-sm border-gray-300 rounded-md"
                            placeholder={med?.strength || "e.g., 1 tablet"}
                            required={
                              logMedAdmin.administrationStatus ===
                              LOG_STATUSES.GIVEN
                            }
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div>
                <label
                  htmlFor="administeredAtDateTime"
                  className="block text-sm font-medium text-gray-700"
                >
                  Administered At
                </label>
                <input
                  type="datetime-local"
                  id="administeredAtDateTime"
                  name="administeredAtDateTime"
                  value={logMedAdmin.administeredAtDateTime}
                  onChange={handleLogInputChange}
                  className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label
                  htmlFor="logNotes"
                  className="block text-sm font-medium text-gray-700"
                >
                  Notes
                </label>
                <textarea
                  id="logNotes"
                  name="logNotes"
                  rows="3"
                  value={logMedAdmin.logNotes}
                  onChange={handleLogInputChange}
                  className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  placeholder="e.g., Taken with food."
                ></textarea>
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <button
                type="button"
                onClick={cancelEditLogEntry}
                className="mr-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {isEditingLogEntry ? "Update Log" : "Save Log"}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="mb-8">
        <input
          type="text"
          placeholder="Search history by medication or notes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full p-2 mb-4 border border-gray-300 rounded-lg"
        />

        {isLoadingAdministrationHistory ? (
          <p>Loading history...</p>
        ) : filteredHistory.length > 0 ? (
          <div>
            {filteredHistory.slice(0, visibleHistoryCount).map((logEntry) => {
              const logStatus = getLogStatus(logEntry);
              const contextBp = logEntry.vitalContext?.bloodPressure;
              return (
                <div
                  key={logEntry.id}
                  className="mb-4 p-4 border border-gray-200 rounded-lg bg-white shadow-sm"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-grow">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="font-bold text-lg text-gray-800">
                          {logEntry.medicationName}
                        </h4>
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-semibold ${getStatusBadgeClasses(logStatus)}`}
                        >
                          {logStatus}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        Dosage:{" "}
                        {logStatus === LOG_STATUSES.GIVEN
                          ? logEntry.dosageAdministered || "N/A"
                          : "Not administered"}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        {formatAdministeredAt(logEntry.administeredAt)}
                      </p>

                      {logEntry.holdReason && (
                        <p className="mt-2 text-sm text-amber-800">
                          Reason: {logEntry.holdReason}
                        </p>
                      )}

                      {contextBp && (
                        <p className="mt-1 text-sm text-blue-700">
                          BP context: {formatBloodPressure(contextBp)}
                        </p>
                      )}

                      {logEntry.notes && (
                        <p className="mt-2 text-sm text-gray-700">
                          Notes: {logEntry.notes}
                        </p>
                      )}
                    </div>
                    {canWrite && (
                      <div className="flex-shrink-0 ml-4 flex flex-row space-x-2 items-center">
                        <button
                          onClick={() => handleEditLogEntry(logEntry)}
                          className="text-blue-500 hover:text-blue-700 p-1"
                        >
                          <FaEdit size={20} />
                        </button>
                        <button
                          onClick={() => handleDeleteLogEntry(logEntry.id)}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <FaTrash size={20} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {visibleHistoryCount < filteredHistory.length && (
              <div className="text-center mt-4">
                <button
                  onClick={handleLoadMore}
                  className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg shadow-md transition-colors duration-300"
                >
                  Load More
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-10">
            <p className="text-gray-500">No administration history found.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MedicationAdministration;
